// Builds the payload each ad platform actually reads, from the one GA4-shaped object a
// screen hands `trackEvent`. Two things decide what comes out:
//
//   - **Not on the platform's standard list => that platform's custom event.** A name a
//     platform does not know is not a free no-op: sent through `track` it lands in Meta's
//     Events Manager as an *invalid standard event*, and Reddit drops it outright. TikTok
//     is the lenient one — it files an unlisted name as a custom event by itself — but it
//     goes through the same rule so one sentence covers all three.
//   - **Nobody but GA4 reads `items`.** Meta wants `content_ids`/`content_type`, Reddit
//     wants `products`/`itemCount`, TikTok wants `contents`/`content_type`, so until those
//     were sent no checkout event carried product identity on any of the three and catalog
//     ads had nothing to match on.
//
// The product a catalog ad matches is the **event**, not a ticket type — the agency
// keys one catalog product per numeric VenuePilot event id, the only identifier its feed
// and checkout both agree on. Hence a single-product payload built from the event id
// rather than from `items`.
//
// packages/checkout/doc/claude/analytics.md carries the full contract in prose, and what
// still has to be set up by hand in each platform's ads manager.

import { asEventId } from './eventDataLayer';
import type { EventName } from './useTracking';

/**
 * The GA4-shaped object a screen passes to `trackEvent`. Only what a pixel reads is
 * declared, and `items` stays `unknown` because it is counted, never forwarded.
 *
 * Not exported: nothing outside builds one — screens pass object literals and
 * `trackEvent` takes `unknown`.
 */
type TrackingPayload = {
  currency?: string;
  value?: number;
  items?: unknown;
};

export type MetaCommand = {
  command: 'track' | 'trackCustom';
  name: string;
  payload: Record<string, unknown>;
};

/**
 * Undiscriminated on purpose, unlike `RedditCommand` below: TikTok has one command, and a
 * custom event's name is simply the event's own name, so there is no second arm to keep
 * honest.
 */
export type TikTokCommand = {
  name: string;
  payload: Record<string, unknown>;
};

export type RedditStandardEvent = 'ViewContent' | 'AddToCart' | 'Purchase';

/**
 * Discriminated on `name` so `Custom` cannot be emitted without the name it stands in
 * for: Reddit reports every `Custom` hit under `customEventName`, and one sent without it
 * is an unnamed row in the advertiser's dashboard.
 *
 * The standard arm has to be the literal union. `Exclude<string, 'Custom'>` collapses
 * back to `string` — conditional types distribute over unions, and `string` is not one —
 * and the discrimination silently stops working.
 */
export type RedditCommand =
  | { name: 'Custom'; payload: { customEventName: string } & Record<string, unknown> }
  | { name: RedditStandardEvent; payload: Record<string, unknown> };

/**
 * Meta's standard events, mapped from ours; anything absent goes out through
 * `fbq('trackCustom', …)`. Meta's own list is longer, but an event checkout never fires
 * cannot be sent wrong. https://developers.facebook.com/docs/meta-pixel/reference
 */
const META_STANDARD_EVENTS: Partial<Record<EventName, string>> = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
  add_payment_info: 'AddPaymentInfo',
};

/**
 * Reddit's standard events, mapped from ours.
 *
 * Reddit's list is short, and shorter than Meta's exactly where it matters. In full:
 * PageVisit, ViewContent, Search, AddToCart, AddToWishlist, Purchase, Lead, SignUp,
 * Custom. So `InitiateCheckout` and `AddPaymentInfo` — standard on Meta — must travel as
 * `Custom` here. The provider's own `rdt('track', 'PageVisit')` depends on the same list.
 * https://github.com/reddit/reddit-gtm-template
 */
const REDDIT_STANDARD_EVENTS: Partial<Record<EventName, RedditStandardEvent>> = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  purchase: 'Purchase',
};

/**
 * The name a Reddit custom event travels under, where the industry name beats ours.
 * Reddit-only by construction: both entries are *standard* on Meta, so Meta never
 * reaches for a replacement name. Everything else keeps its GA4 name, so only the
 * *command* changes and no custom conversion an advertiser already built gets renamed.
 */
const REDDIT_CUSTOM_EVENT_NAMES: Partial<Record<EventName, string>> = {
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
};

/**
 * TikTok's standard events, mapped from ours. Checked 2026-08-21 against
 * https://ads.tiktok.com/help/article/standard-events-parameters?lang=en. The URL and the
 * date rather than a pasted list: TikTok's list keeps moving, and a snapshot that has
 * quietly mixed vintages reads as confirmed, which is worse than no snapshot at all.
 *
 * TikTok has **no `trackCustom`**. Everything leaves through `ttq.track`, and a name that
 * is not on TikTok's list is filed as a custom event and reported under Event Type in
 * Events Manager. So the fallback to the GA4 name *is* the custom path here — the opposite
 * of Meta, where an unknown name sent through `track` lands as an invalid standard event.
 *
 * `purchase` is **`Purchase`**, not the `PlaceAnOrder` this map carried until sc-19599.
 * Sticking to what TikTok's migration page actually says: `CompletePayment` was *renamed*
 * to `Purchase` in May 2025 and legacy names of that kind are auto-converted on the
 * backend, whereas `ClickButton` and `PlaceAnOrder` are **soft-deprecated with no direct
 * replacement** — supported to 2027, dropped from the docs in 2025, and their history
 * cannot be carried over to a new event. So `PlaceAnOrder` is not an old spelling of
 * `Purchase` that TikTok quietly fixes for us; it is a different, retiring event, and
 * `Purchase` is the current name for what ours reports — a completed payment, fired from
 * `CheckoutFinalize.onCompleted`. What the old name cost is not visible from here, and no
 * history moves across with the rename.
 * https://ads.tiktok.com/help/article/how-to-adopt-tiktoks-updated-standard-events
 *
 * These are the **browser pixel**'s names, the same caveat Reddit carries below: TikTok's
 * Events API is a server-side surface we do not use and its fields differ. `ttq.track` also
 * takes a third `options` argument whose `event_id` is that API's deduplication key — the
 * direct analogue of Meta's `order_id`, and unsent follow-up work for the same reason.
 */
const TIKTOK_STANDARD_EVENTS: Partial<Record<EventName, string>> = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
  add_payment_info: 'AddPaymentInfo',
};

/**
 * The events that carry catalog parameters, on all three pixels, whether that platform
 * sends them as standard or custom. `add_payment_info` is one of them: it is a commerce
 * event a catalog ad consumes, and "standard or custom" is exactly its situation — Meta
 * and TikTok know `AddPaymentInfo`, Reddit does not and sends it as `Custom`, and the
 * parameters travel either way.
 *
 * Two names are deliberately absent. `remove_from_cart`, because a removal is not a
 * signal a catalog ad consumes; `access_code_applied`, because it is not a commerce event
 * at all and fires with no payload.
 */
const CATALOG_EVENTS: EventName[] = [
  'view_item',
  'add_to_cart',
  'begin_checkout',
  'add_payment_info',
  'purchase',
];

/**
 * Sent because the failure mode is asymmetric, not because we have a use for it. Reddit's
 * product object takes `id`, `name` and `category`; sources disagree on whether
 * `category` is merely recommended or whether an entry without one is **dropped**. If it
 * is dropped, the array is discarded server-side while the pixel call still looks
 * perfectly healthy in the browser — an invisible failure — whereas sending an extra
 * constant costs nothing. A constant suffices because every catalog product *is* one
 * event, and matching keys on `id`.
 * https://business.reddithelp.com/s/article/about-event-metadata
 */
const REDDIT_PRODUCT_CATEGORY = 'event';

/**
 * `'product'`, because `content_id` is a catalog SKU id. `'product_group'` is the value for
 * an item-group id, and the feed keys one product per event rather than grouping them.
 */
const TIKTOK_CONTENT_TYPE = 'product';

/** `access_code_applied` fires with no payload, so a non-object is `{}`, never a throw. */
const asPayload = (data: unknown): TrackingPayload => (
  typeof data === 'object' && data !== null ? data as TrackingPayload : {}
);

/**
 * The money keys, **whitelisted rather than spread**. Spreading would put GA4's keys —
 * `items`, which none of the three pixels reads, and `transaction_id`, which none of them
 * accepts — on three more ad networks' queues. `transaction_id` is now the public order id
 * rather than the order fingerprint, so this is defence in depth rather than the barrier it
 * once was; keep it anyway, and do not "simplify" it into `...data`.
 *
 * `!== undefined`, not `in` and not truthiness. `CheckoutFinalize` sends
 * `value: orderSummary.breakdown?.orderTotal`, so the key is *present and undefined*
 * when the breakdown is missing, and `fbq`/`rdt`/`ttq` serialise that into a parameter the
 * platform rejects. (`EventDetails` spreads its `value` in conditionally, which is the
 * pattern to copy — but the guard cannot depend on every call site remembering to.)
 * Truthiness would be wrong for the opposite reason: a legitimate `value: 0` has to
 * survive.
 */
const moneyParams = ({ currency, value }: TrackingPayload): Record<string, unknown> => ({
  ...(currency === undefined ? {} : { currency }),
  ...(value === undefined ? {} : { value }),
});

/** One item's quantity, or 0 for anything that is not a usable number. */
const quantityOf = (entry: unknown): number => {
  const quantity = (entry as { quantity?: unknown } | null)?.quantity;

  return typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : 0;
};

/**
 * How many tickets the payload is about, for Reddit's `itemCount` and the `quantity` in
 * TikTok's `contents` entry.
 *
 * Falls back to 1, covering two cases that both mean one product: `view_item` reports the
 * page's whole catalogue, whose rows carry no `quantity` at all (`BlueprintTrackingItem`
 * omits the key — a catalogue view has no quantity to report), so the sum is 0; and a
 * payload with no `items` is still a hit about this one event. The narrowing is defensive
 * because `items` arrives from an `unknown` parameter and a throw here would take down
 * every pixel event on the page — the provider's try/catch guards the *call*, not the
 * payload build.
 */
const catalogItemCount = ({ items }: TrackingPayload): number => {
  const counted = Array.isArray(items) ? items.reduce<number>((sum, entry) => sum + quantityOf(entry), 0) : 0;

  return counted || 1;
};

/** Meta's catalog parameters, or nothing when there is no usable event id. */
const metaCatalogParams = (
  event: EventName,
  eventId?: number | string,
): Record<string, unknown> => {
  const id = asEventId(eventId);

  if (id === undefined || !CATALOG_EVENTS.includes(event)) {
    return {};
  }

  // A string array: Meta matches catalog ids as strings, and a number stops matching
  // silently rather than erroring.
  return { content_ids: [String(id)], content_type: 'product' };
};

/** Reddit's catalog parameters, or nothing when there is no usable event id. */
const redditCatalogParams = (
  event: EventName,
  payload: TrackingPayload,
  eventId?: number | string,
): Record<string, unknown> => {
  const id = asEventId(eventId);

  if (id === undefined || !CATALOG_EVENTS.includes(event)) {
    return {};
  }

  // `quantity` repeats `itemCount` on purpose: Reddit asks for the event-level count and
  // the product-level one on revenue events, and with exactly one product they are the
  // same number by construction.
  //
  // Two documented product fields are knowingly left off. `itemPrice`, because the
  // product is the event, not a ticket type, so it would be an average across differently
  // priced types where `value` already carries the money exactly. `name`, because it
  // would have to track the feed's title to be worth anything, while matching keys on
  // `id` — the cost is that Ads Manager rows read as a bare number.
  const itemCount = catalogItemCount(payload);

  return {
    products: [{ id: String(id), category: REDDIT_PRODUCT_CATEGORY, quantity: itemCount }],
    itemCount,
  };
};

/**
 * TikTok's catalog parameters, or nothing when there is no usable event id.
 *
 * **The id is sent twice, and the second copy is defensive rather than documented.** The
 * documented path is `contents[].content_id`, which is what the parameter reference
 * describes and what this sends. The extra top-level `content_id` comes from the
 * catalog-match troubleshooting page, which insists the key "should be spelled as
 * content_id instead of content_ids" and illustrates one item as `{content_id: '12345'}`
 * against several as `{content_id: '12345'},{content_id: '67890'}` — notation that may
 * simply be describing the `contents` entries. Read the other way it names a flat key, and
 * the reference documents the top level as `content_ids` (plural, an array) rather than
 * either. Rather than pick a reading, send both: an unrecognised key is inert, and the
 * failure on the other side is invisible — an unmatched payload looks perfectly healthy in
 * the browser, and there is no TikTok advertiser to notice it matched nothing. Sending the
 * same id twice cannot disturb the money, which is the single top-level `value`; at worst a
 * platform that folds the flat key into an extra entry sees one duplicate id, which is a
 * reporting artefact rather than a billing one.
 * https://ads.tiktok.com/help/article/how-to-troubleshoot-catalog-match-rate-issues
 *
 * `content_type` is the same trade in miniature. The reference documents it at the top
 * level, where the dynamic-showcase formats this feeds require it; repeating it inside each
 * entry is ours, on the same "inert if ignored" reasoning, and in the same spirit as
 * Reddit's `category` above.
 *
 * Four documented parameters are knowingly left off; check here before "fixing" one.
 *
 *   - `content_ids` — TikTok says in as many words to spell the key `content_id`, so the
 *     plural is the wrong name rather than a second one.
 *   - `price` — sc-19599 asked for it by name and it is omitted anyway. The one catalog
 *     product is the *event*, not a ticket type, so a single price would be an average
 *     across differently priced tiers while `value` already carries the money exactly, and
 *     a `price` beside a `quantity` invites TikTok to read `price × quantity` as a second,
 *     disagreeing statement of it. Same call as Reddit's `itemPrice`.
 *   - `content_name` — would have to track the feed's title to be worth anything, and
 *     matching keys on the id. Same call as Reddit's `name`.
 *   - top-level `quantity` — a per-product number, and the product's quantity already
 *     travels inside `contents`. Reddit sends its count twice and TikTok sends it once,
 *     which is not an inconsistency: Reddit documents `itemCount` as a distinct
 *     *event-level* field, whereas TikTok's top-level `quantity` is the per-product key
 *     under another roof.
 */
const tikTokCatalogParams = (
  event: EventName,
  payload: TrackingPayload,
  eventId?: number | string,
): Record<string, unknown> => {
  const id = asEventId(eventId);

  if (id === undefined || !CATALOG_EVENTS.includes(event)) {
    return {};
  }

  // A string, as for Meta: TikTok matches the id against the feed's SKU id exactly, and a
  // number stops matching silently rather than erroring.
  const contentId = String(id);

  return {
    content_id: contentId,
    content_type: TIKTOK_CONTENT_TYPE,
    contents: [{
      content_id: contentId,
      content_type: TIKTOK_CONTENT_TYPE,
      quantity: catalogItemCount(payload),
    }],
  };
};

/** What to hand `fbq` for one of our events. */
export const metaCommand = (
  event: EventName,
  data: unknown,
  eventId?: number | string,
): MetaCommand => {
  const payload = asPayload(data);
  const standardName = META_STANDARD_EVENTS[event];

  return {
    command: standardName ? 'track' : 'trackCustom',
    name: standardName ?? event,
    payload: {
      ...moneyParams(payload),
      ...metaCatalogParams(event, eventId),
    },
  };
};

/**
 * What to hand `rdt` for one of our events. Always dispatched as
 * `rdt('track', name, payload)` — Reddit has no separate custom command the way Meta
 * does; `Custom` *is* one of its standard names, and the real name rides in the payload.
 *
 * These are the **browser pixel**'s names. Reddit's Conversions API differs twice over,
 * and getting one right while missing the other is the trap: snake_case for the same
 * fields (`custom_event_name`, `item_count`), *and* money split into `value` (integer,
 * smallest currency unit) and `value_decimal`, where the pixel's `value` is a decimal
 * amount.
 */
export const redditCommand = (
  event: EventName,
  data: unknown,
  eventId?: number | string,
): RedditCommand => {
  const payload = asPayload(data);
  const params = {
    ...moneyParams(payload),
    ...redditCatalogParams(event, payload, eventId),
  };
  const standardName = REDDIT_STANDARD_EVENTS[event];

  if (standardName) {
    return { name: standardName, payload: params };
  }

  return {
    name: 'Custom',
    payload: { ...params, customEventName: REDDIT_CUSTOM_EVENT_NAMES[event] ?? event },
  };
};

/**
 * What to hand `ttq` for one of our events. Always dispatched as `ttq.track(name, payload)`:
 * TikTok has a single command, and a name it does not know becomes a custom event rather
 * than a rejected standard one — see `TIKTOK_STANDARD_EVENTS`.
 */
export const tikTokCommand = (
  event: EventName,
  data: unknown,
  eventId?: number | string,
): TikTokCommand => {
  const payload = asPayload(data);

  return {
    name: TIKTOK_STANDARD_EVENTS[event] ?? event,
    payload: {
      ...moneyParams(payload),
      ...tikTokCatalogParams(event, payload, eventId),
    },
  };
};
